import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Leaf, Sparkles, Infinity as InfinityIcon } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1580334571318-a51dfe93f27f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxoZWFsdGh5JTIwZm9vZCUyMGdyZWVuJTIwdmVnZXRhYmxlcyUyMGNhbGNpdW0lMjByaWNoJTIwYm9uZSUyMGhlYWx0aHxlbnwwfHx8fDE3ODkyMjQyMTd8MA&ixlib=rb-4.1.0&q=85";

const TRUST = [
  { icon: ShieldCheck, label: "Wissenschaftlich geprüft" },
  { icon: Leaf, label: "100 % natürlicher Ansatz" },
  { icon: Sparkles, label: "Fokus Darm-Knochen-Achse" },
  { icon: InfinityIcon, label: "Einmalzahlung – dauerhaft" },
];

export default function Hero() {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative overflow-hidden fb-grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-[#E2EBE1] text-[#2D5A27] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 mb-6">
            <Leaf className="w-3.5 h-3.5" /> Ernährung bei Osteoporose
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#1C2D24]">
            Starke Knochen durch gezielte <span className="text-[#2D5A27]">Ernährung</span> &amp; Probiotika
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[#4A5D52] max-w-xl">
            Wissenschaftlich fundierte Erkenntnisse, Naturprodukte und konkrete Ernährungspläne bei Osteoporose –
            verständlich erklärt für jedes Lebensalter.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-cta-myths"
              onClick={() => scrollTo("mythen")}
              className="bg-[#1E3F20] hover:bg-[#163017] text-white rounded-full px-7 h-12 text-base"
            >
              Kostenlose Mythen &amp; Fakten lesen
            </Button>
            <Button
              data-testid="hero-cta-checkout"
              onClick={() => navigate("/premium")}
              variant="outline"
              className="border-[#C87D55] text-[#C87D55] hover:bg-[#C87D55] hover:text-white rounded-full px-7 h-12 text-base"
            >
              Premium sichern – 19,99 €
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {TRUST.map((t) => (
              <div key={t.label} className="flex items-center gap-2 text-sm text-[#4A5D52]">
                <t.icon className="w-4 h-4 text-[#769777] shrink-0" />
                <span>{t.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-[#E2DDD3]">
            <img src={HERO_IMG} alt="Knochengesunde Ernährung" className="w-full h-[380px] lg:h-[480px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E3F20]/40 to-transparent" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 ring-1 ring-[#E2DDD3] hidden sm:block">
            <p className="text-xs uppercase tracking-widest text-[#769777] font-semibold">Probiotika-Effekt</p>
            <p className="font-display text-2xl font-bold text-[#1E3F20]">+8,4 μg/L</p>
            <p className="text-xs text-[#4A5D52]">Knochenformation (P1NP)</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
